import { DeleteRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { debounce } from 'lodash';
import moment from 'moment';
import { useDialog } from 'muibox';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import BackNavigationButton from '../../components/common/BackNavigationButton';
import { NavigationGuard } from '../../components/common/NavigationGuard';
import OnlyFansQuillMessageEditor from '../../components/common/OnlyFansQuillMessageEditor';
import PageContainer from '../../components/common/PageContainter';
import {
    readInventoryTemplateSortAlphabetically,
    writeInventoryTemplateSortAlphabetically,
} from '../../components/common/inventoryTemplateSortPreference';
import StyledCardTitleBar from '../../components/common/StyledCardTitleBar';
import TextInputDialog from '../../components/common/TextInputDialog';
import { distinctInventoryTags } from '../../components/common/inventoryTemplateSelection';
import InventoryPostSuccessDialog from '../../components/inventory/InventoryPostSuccessDialog';
import ListMultiSelect from '../../components/inventory/ListMultiSelect';
import ListSingleSelect from '../../components/inventory/ListSingleSelect';
import PostLabelMultiSelect from '../../components/inventory/PostLabelMultiSelect';
import ContentTagsCard from '../../components/vault/ContentTagsCard';
import MediaHolder from '../../components/vault/MediaHolder';
import VaultTile from '../../components/vault/VaultTile';
import useInventory, { ContentInventory, InventoryVersion } from '../../hooks/useInventory';
import useInventoryPostToOnlyFans from '../../hooks/useInventoryPostToOnlyFans';
import useMessageUsersMassDmList, {
    SextforceAutoMassDmMessage,
    SextforceAutoMassDmMessageListSortBy,
    SextforceAutoMassDmMessageStatus,
} from '../../hooks/useMessageUsersMassDmList';
import useSextforceBigBrotherRestrictedWords from '../../hooks/useSextforceBigBrotherRestrictedWords';
import useSubscriber, { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import useSubscriberLists from '../../hooks/useSubscriberLists';
import useSubscriberPosts from '../../hooks/useSubscriberPosts';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditonal from '../../hooks/useSubscriberVaultMediasAdditional';
import { SettingsContext } from '../../store/SettingsContext';
import { stripHtml } from '../../utils/common';

type AutoCompleteOption = {
    inventoryName: string;
    inventoryId: number | string;
    versionId: number;
    versionIndex: number;
    versionName: string;
    tags: string[];
    text: string;
};
/**
 * NOTE ABOUT PageContainer
 * In your app, replace the lightweight fallback below
 * with your real PageContainer (e.g. `import PageContainer from '@/components/PageContainer'`).
 */
const FallbackPageContainer: React.FC<React.PropsWithChildren<{ subscriber: OnlyFansSubscriber; title: string }>> = ({
    subscriber,
    title,
    children,
}) => {
    const settingsContext = useContext(SettingsContext);

    return (
        <PageContainer maxWidth="lg">
            <Typography variant="h5" sx={{ mb: 2 }}>
                <BackNavigationButton url={`/subscribers/${subscriber?._id}/${settingsContext.services.inventory.homeUrl}`} /> Templates for{' '}
                {subscriber?.username}
            </Typography>
            {children}
        </PageContainer>
    );
};

// ---- Main Page ----
const ContentInventoryPage: React.FC<{ PageContainerComponent?: React.ComponentType<React.PropsWithChildren<{ title?: string }>> }> = ({
    PageContainerComponent = FallbackPageContainer,
}) => {
    const theme: Theme = useTheme();
    const dialog = useDialog();

    // inventories list + selection
    const {
        data: contentInventory,
        refetch: refreshContentInventory,
        isLoading: isInventoryLoading,
        newContentInventoryItem,
        newContentInventoryVersion,
        mergeSendLists,
        mergePostLabels,
        mergeTaggedCreators,
        mergeConsentForms,
        updateInventory,
        deleteInventory,
    } = useInventory();

    const [inventories, setInventories] = useState<ContentInventory[]>([]);
    const [selectedInventoryId, setSelectedInventoryId] = useState<number | ''>('');
    const [sortAlphabetically, setSortAlphabetically] = useState(readInventoryTemplateSortAlphabetically);

    const [model, setModel] = useState<ContentInventory | null>(null);
    const [tabIndex, setTabIndex] = useState(-1);

    // Inline edit for version name on tabs
    const [editingTab, setEditingTab] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [pendingInventoryId, setPendingInventoryId] = React.useState<number | ''>('');
    const [isDirty, setIsDirty] = useState(false);

    // Dialog controls (e.g., add version)
    const [openAddVersion, setOpenAddVersion] = useState(false);
    const [newVersionSourceSubId, setNewVersionSourceSubId] = useState<number | ''>('');
    const [openAddInventory, setOpenAddInventory] = useState(false);
    const [postSuccessOpen, setPostSuccessOpen] = useState(false);
    const [postedUrl, setPostedUrl] = useState('');
    const [newTemplateName, setNewTemplateName] = useState('');
    const [showMassDmList, setShowMassDmList] = useState(false);
    const [massDmExpandedDm, setMassDmExpandedDm] = useState<SextforceAutoMassDmMessage | null>(null);
    const [massDmMediaPreviewItems, setMassDmMediaPreviewItems] = useState<VaultMediaResponseList[] | null>(null);
    const [massDmMediaPreviews, setMassDmMediaPreviews] = useState<Set<number>>(new Set());
    const [isMassDmMediaLoading, setIsMassDmMediaLoading] = useState(false);

    const [selectedOption, setSelectedOption] = useState<AutoCompleteOption | null>(null);

    const {
        // data: listsPages,
        lists,
        hasMore,
        isLoading: listsLoading,
        isFetching: listsFetching,
        refetch: refetchLists,
    } = useSubscriberLists(true);

    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();
    const { data: subscriber, isLoading: isSubscriberLoading } = useSubscriber();
    const { postInventoryVariation, isPosting } = useInventoryPostToOnlyFans();

    const [query, setQuery] = useState<string>('');
    const [massDmSearchInput, setMassDmSearchInput] = useState<string>('');
    const debouncedSetQuery = useMemo(
        () =>
            debounce((val: string) => {
                setOffset(0);
                setAccumulatedMassDms([]);
                setQuery(val);
            }, 500),
        [],
    );
    const [colorId, setColorId] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<SextforceAutoMassDmMessageStatus>('sent');
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(20);
    const [sortBy, setSortBy] = useState<SextforceAutoMassDmMessageListSortBy>('date');
    const [accumulatedMassDms, setAccumulatedMassDms] = useState<SextforceAutoMassDmMessage[]>([]);
    const massDmScrollRef = useRef<HTMLDivElement>(null);
    const {
        data: massDms,
        isFetching: isMassDmsLoading,
        refetch: refetchMassDms,
    } = useMessageUsersMassDmList(query, status, offset, limit, sortBy);

    // Accumulate pages as offset increases
    useEffect(() => {
        if (!massDms?.list) return;
        setAccumulatedMassDms(prev => (offset === 0 ? massDms.list : [...prev, ...massDms.list]));
    }, [massDms]);

    const massDmTotal = massDms?.total ?? 0;

    const handleMassDmScroll = useCallback(() => {
        const el = massDmScrollRef.current;
        if (!el || isMassDmsLoading) return;
        const pct = el.scrollTop / (el.scrollHeight - el.clientHeight);
        if (pct >= 0.5 && accumulatedMassDms.length < massDmTotal) {
            setOffset(prev => prev + limit);
        }
    }, [isMassDmsLoading, accumulatedMassDms.length, massDmTotal, limit]);

    const { getManyVaultMediaFromDatabase, isInventoryVaultItemsLoading } = useSubscribersVaultMediasAdditonal();

    const handleOpenDmPreview = useCallback(
        async (massDm: SextforceAutoMassDmMessage) => {
            setMassDmExpandedDm(massDm);
            const mediaIds: number[] = (massDm.messageDetails?.media ?? []).map((m: { id: number }) => m.id).filter(Boolean);
            if (mediaIds.length === 0) return;
            const freeIds = new Set<number>((massDm.messageDetails?.previews ?? []) as number[]);
            setMassDmMediaPreviews(freeIds);
            setIsMassDmMediaLoading(true);
            setMassDmMediaPreviewItems([]);
            try {
                const items = await getManyVaultMediaFromDatabase(mediaIds);
                setMassDmMediaPreviewItems(items ?? []);
            } finally {
                setIsMassDmMediaLoading(false);
            }
        },
        [getManyVaultMediaFromDatabase],
    );

    const { labels, labelsIsLoading } = useSubscriberPosts();

    const onlyFansPostlabels = labels.data ?? [];

    //loading variables
    const [pageIsLoading, setPageIsLoading] = useState(false);
    const [unlockedItems, setUnlockedItems] = useState<VaultMediaResponseList[]>([]);
    const [lockedItems, setlockedItems] = useState<VaultMediaResponseList[]>([]);

    useEffect(() => {
        // on subscriber switch be sure to clear unlocked and locked items
        setUnlockedItems([]);
        setlockedItems([]);
    }, [subscriber, subscriber?._id]);

    useEffect(() => {
        if (contentInventory) {
            setInventories(contentInventory);
        }
    }, [contentInventory]);

    // Prompt the user before leaving the page if there are unsaved changes
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!isDirty) return;
            // const normalizeInventory = (inv: ContentInventory): ContentInventory => ({
            //     ...inv,
            //     versions: inv.versions.map(v => ({
            //         ...v,
            //         unlockedMedias: (v.unlockedMedias ?? []).map(({ mediaId, order }) => ({ mediaId, order })),
            //         lockedMedias: (v.lockedMedias ?? []).map(({ mediaId, order }) => ({ mediaId, order })),
            //         sendLists: (v.sendLists ?? []).map(sl => ({ listId: sl.listId })),
            //         excludeLists: (v.excludeLists ?? []).map(sl => ({ listId: sl.listId })),
            //         creatorTags: (v.creatorTags ?? []).map(({ creatorOnlyfansId, creatorName }) => ({ creatorOnlyfansId, creatorName })),
            //     })),
            // });

            e.preventDefault();

            e.returnValue = ''; // required for Chrome
        };

        window.addEventListener('beforeunload', handler);

        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // --- Inventory Select Change Handling with Dirty Check ---
    const applyInventorySelection = React.useCallback(
        (val: number | '') => {
            setSelectedInventoryId(val);

            if (val !== '') {
                const found = inventories.find(i => i.inventoryId === val);

                if (found) {
                    setModel(found);
                    setTabIndex(0);
                }
            }

            // once you intentionally switch, you're no longer "dirty" relative to the new base
            setIsDirty(false);
        },
        [inventories],
    );

    // --- Select onChange handler ---
    // const handleInventorySelectChange = React.useCallback(
    //     (e: SelectChangeEvent<any>) => {
    //         const val = e.target.value as number | '';

    //         if (!isDirty) {
    //             applyInventorySelection(val);
    //         } else {
    //             setPendingInventoryId(val);
    //             setConfirmOpen(true);
    //         }
    //     },
    //     [isDirty, applyInventorySelection],
    // );
    const handleInventorySelectChange = useCallback(
        (id: number, versionIndex: number) => {
            if (!isDirty) {
                applyInventorySelection(id !== 0 ? id : '');
                setTabIndex(versionIndex);
            } else {
                setPendingInventoryId(id !== 0 ? id : '');
                setConfirmOpen(true);
            }
        },
        [isDirty, applyInventorySelection],
    );

    const handleRootChange = <K extends keyof ContentInventory>(key: K, val: ContentInventory[K]) => {
        setModel(m => ({ ...m, [key]: val } as ContentInventory));
    };

    const handleVersionChange = (idx: number, patch: Partial<InventoryVersion>) => {
        setModel(m => {
            if (!m) return m;
            const versions = [...m.versions];

            versions[idx] = { ...versions[idx], ...patch };

            return { ...m, versions } as ContentInventory;
        });
    };

    const addInventoryItem = async (name: string, massMessageId?: number) => {
        setPageIsLoading(true);

        const finalName = name?.trim() ? name.trim() : `Untitled Inventory`;
        const newInv: ContentInventory | null = await newContentInventoryItem(finalName, 'Variation 1', massMessageId);

        if (!newInv) {
            setPageIsLoading(false);
            return;
        }

        // setInventories((list) => [...list, newInv]);
        await refreshContentInventory();

        // React 17 (legacy render) doesn't auto-batch state updates that happen after an `await`, so each of
        // these would render separately. That leaves an intermediate render where `selectedInventoryId` has
        // changed but `model` is still the PREVIOUS template — the media-load effect then fires against the old
        // version and re-loads the old media, which is why a brand-new template kept the previous selection's
        // media. Batch them into one render (with the media explicitly cleared) so that never happens.
        unstable_batchedUpdates(() => {
            setSelectedInventoryId(newInv.inventoryId);
            setModel(newInv);
            setTabIndex(0);
            // A brand-new template starts empty — clear any media carried over from the previous selection.
            setUnlockedItems([]);
            setlockedItems([]);
            setOpenAddInventory(false);
            setShowMassDmList(false);
        });
        setPageIsLoading(false);
    };

    // --- Add Version ---
    const addVersion = async (versionName: string, sourceSubId?: number) => {
        if (!model) return;

        setPageIsLoading(true);
        setOpenAddVersion(false);
        setNewVersionSourceSubId('');

        // Duplicating copies the SOURCE variation from the database. If the user just built a brand-new
        // template (or edited a variation) and hasn't saved, that DB copy is still empty — so the duplicate
        // comes over blank. Persist the current in-memory template first when duplicating (this is exactly
        // the "save first, then duplicate" workaround, automated). Plain new (non-duplicate) versions skip
        // this since they start empty anyway.
        if (sourceSubId !== undefined) {
            const saved = await updateInventory(model);

            if (!saved) {
                enqueueSnackbar('Could not save the template before duplicating. Please try again.', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
                setPageIsLoading(false);
                return;
            }
        }

        const newVersionDatabase = await newContentInventoryVersion(model.inventoryId, versionName, sourceSubId);

        if (newVersionDatabase) {
            const updated = { ...model, versions: [...model.versions, newVersionDatabase] } as ContentInventory;
            const newIndex = model.versions.length;

            // Batch together — React 17 (legacy render) doesn't auto-batch after an `await`, so the model and
            // the new tab index would otherwise land in separate renders. Keeping them in one render (and
            // clearing media for a fresh version) stops the media-load effect from writing the previous
            // version's media into the newly-created one.
            unstable_batchedUpdates(() => {
                setModel(updated);
                setTabIndex(newIndex);

                // A fresh (non-duplicated) version starts empty — clear media carried over from the current
                // version. Duplicates intentionally keep the copied media, which the load effect will fetch.
                if (!sourceSubId) {
                    setUnlockedItems([]);
                    setlockedItems([]);
                }

                if (selectedInventoryId !== '') {
                    setInventories(list => list.map(inv => (inv.inventoryId === model.inventoryId ? updated : inv)));
                }
            });
        }

        await refreshContentInventory();

        setPageIsLoading(false);
    };

    // --- Delete Inventory Item ---
    const deleteInventoryItem = async () => {
        if (!model) return;

        setPageIsLoading(true);

        await deleteInventory(model.inventoryId)
            .then(() => {
                enqueueSnackbar('Inventory Deleted Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            })
            .catch(() => {
                enqueueSnackbar('Error Deleting Inventory!', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });

        await refreshContentInventory();

        const evt = {
            target: { value: '' } as any,
            currentTarget: { value: '' } as any,
        } as unknown as SelectChangeEvent;

        handleInventorySelectChange(0, 0);
        setPageIsLoading(false);
    };

    // --- Delete Version ---
    const deleteVersion = async (idx: number) => {
        setModel(m => {
            if (!m) return m;

            const updated = { ...m, versions: m.versions.filter((_, i) => i !== idx) } as ContentInventory;
            // if (selectedInventoryId !== '') {
            //   setInventories((list) => list.map((inv) => (inv.inventoryId === m.inventoryId ? updated : inv)));
            // }

            return updated;
        });

        // await refreshContentInventory();
        setTabIndex(t => (t > 0 ? t - 1 : 0));
    };

    const reset = () => {
        if (selectedInventoryId === '') return;
        const found = inventories.find(i => i.inventoryId === selectedInventoryId);
        if (found) setModel(found);
    };

    // --- Save Inventory ---
    const save = async () => {
        if (!model) return;

        if (selectedInventoryId === '') return;

        setIsSaving(true);

        const payload: ContentInventory = model;
        const success = await updateInventory(payload);

        if (success) {
            enqueueSnackbar('Inventory Saved Successfully!', {
                variant: 'success',
                autoHideDuration: 3000,
            });

            const result = await refreshContentInventory();
            const freshList = result.data;

            if (freshList) {
                const fresh = freshList.find(i => i.inventoryId === model.inventoryId);
                if (fresh) {
                    setModel(fresh);
                }
            }
            setIsDirty(false);
        } else {
            enqueueSnackbar('Error Saving Inventory!', {
                variant: 'error',
                autoHideDuration: 3000,
            });
        }

        setIsSaving(false);
    };

    // Produces a plain comparison snapshot for the dirty check (JSON.stringify), not a real ContentInventory.
    const normalizeInventory = (inv: ContentInventory) => ({
        ...inv,
        versions: inv.versions.map(v => ({
            ...v,
            unlockedMedias: (v.unlockedMedias ?? []).map(({ mediaId, order }) => ({ mediaId, order })),
            lockedMedias: (v.lockedMedias ?? []).map(({ mediaId, order }) => ({ mediaId, order })),
            // String-normalize ids so a number-vs-string drift across the save round trip doesn't
            // register as a difference against the freshly fetched copy.
            sendLists: (v.sendLists ?? []).map(sl => ({ listId: String(sl.listId) })),
            excludeLists: (v.excludeLists ?? []).map(sl => ({ listId: String(sl.listId) })),
            creatorTags: (v.creatorTags ?? []).map(({ creatorOnlyfansId, creatorName }) => ({ creatorOnlyfansId, creatorName })),
            tags: (v.tags ?? []).map(({ tagText }) => ({ tagText })),
            postLabels: (v.postLabels ?? []).map(({ postLabelId, labelName }) => ({ postLabelId, labelName })),
        })),
    });

    // this is needed because new variations/versions come in different json order. grr.
    const sortObject = (obj: any): any => {
        if (Array.isArray(obj)) {
            return obj.map(sortObject);
        } else if (obj && typeof obj === 'object') {
            return Object.keys(obj)
                .sort()
                .reduce((acc, key) => {
                    acc[key] = sortObject(obj[key]);
                    return acc;
                }, {} as any);
        }
        return obj;
    };

    // mark dirty whenever model changes relative to inventories[selected]
    useEffect(() => {
        const saved = inventories.find(i => i.inventoryId === selectedInventoryId);

        if (!saved) {
            setIsDirty(false);
            return;
        }

        const a = normalizeInventory(saved);
        const b = model ? normalizeInventory(model) : null;
        // console.log('a', a);
        // console.log('b', b);
        if (!b) {
            setIsDirty(false);
            return;
        }
        // console.log('isDirty?', JSON.stringify(sortObject(a)) !== JSON.stringify(sortObject(b)));
        setIsDirty(JSON.stringify(sortObject(a)) !== JSON.stringify(sortObject(b)));
    }, [inventories, selectedInventoryId, model]);

    const selectedVersion = useMemo(() => model?.versions[tabIndex], [model?.versions, tabIndex]);

    const getPostToOnlyFansDisabledReason = (): string | null => {
        if (!selectedVersion) return 'Select a variation to post';
        if (isDirty) return 'Save changes before posting';
        if (!subscriber?.isLoggedIn) return 'Subscriber is not logged in to OnlyFans';
        if (messageHasRestrictedWords) return 'Message contains restricted words';

        return null;
    };

    const handlePostToOnlyFans = async () => {
        if (!model || !selectedVersion || getPostToOnlyFansDisabledReason()) return;

        try {
            await dialog.confirm({
                message: `Post template variation "${selectedVersion.versionName}" to OnlyFans now?`,
                title: 'Post to OnlyFans',
                ok: {
                    color: 'primary',
                    text: 'Post',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            });
        } catch {
            return;
        }

        const result = await postInventoryVariation(model.inventoryId, selectedVersion.inventorySubId);

        if (result?.postUrl) {
            setPostedUrl(result.postUrl);
            setPostSuccessOpen(true);
        }
    };

    // ---- helpers ----
    const listIdsEqual = (a?: Array<{ mediaId: number; order: number }>, b?: Array<{ mediaId: number; order: number }>) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; i++) {
            if (a[i].mediaId !== b[i].mediaId || a[i].order !== b[i].order) return false;
        }

        return true;
    };

    const toInventoryList = (items: VaultMediaResponseList[]) => items.map((it, order) => ({ mediaId: it.id, order }));

    // Keep a stable loader
    const loadVersionMedias = useCallback(async (v: InventoryVersion) => {
        const unlockedIds = (v.unlockedMedias ?? []).map(m => m.mediaId);
        const lockedIds = (v.lockedMedias ?? []).map(m => m.mediaId);

        // short-circuit
        if (unlockedIds.length === 0 && lockedIds.length === 0) {
            setUnlockedItems([]);
            setlockedItems([]);

            return;
        }

        const [unlockedObjs, lockedObjs] = await Promise.all([
            unlockedIds.length ? getManyVaultMediaFromDatabase(unlockedIds) : Promise.resolve([]),
            lockedIds.length ? getManyVaultMediaFromDatabase(lockedIds) : Promise.resolve([]),
        ]);

        // preserve persisted order
        const unlockedById = new Map(unlockedObjs?.map(m => [m.id, m]));
        const lockedById = new Map(lockedObjs?.map(m => [m.id, m]));

        const unlockedOrdered = (v.unlockedMedias ?? [])
            .map(({ mediaId }) => unlockedById.get(mediaId))
            .filter(Boolean) as VaultMediaResponseList[];

        const lockedOrdered = (v.lockedMedias ?? [])
            .map(({ mediaId }) => lockedById.get(mediaId))
            .filter(Boolean) as VaultMediaResponseList[];

        setUnlockedItems(unlockedOrdered);
        setlockedItems(lockedOrdered);
    }, []);

    // ---- 1) LOAD when inventory or tab changes (NOT when version object identity changes) ----
    useEffect(() => {
        if (!model) return;

        const v = model.versions[tabIndex];

        if (!v) return;

        loadVersionMedias(v);

        // deps: only the selection (inventory + tab) and the stable loader
    }, [selectedInventoryId, tabIndex]);

    // ---- 2) SAVE when lists change, compare against current version snapshot, avoid loops ----

    // We track the last payload we wrote for this tab to avoid re-writing identical data
    const lastWriteRef = React.useRef<{ tab: number; unlockedSig: string; lockedSig: string } | null>(null);

    useEffect(() => {
        if (!model) return;

        const v = model.versions[tabIndex];

        if (!v) return;

        const nextUnlocked = toInventoryList(unlockedItems);
        const nextLocked = toInventoryList(lockedItems);

        // If nothing actually changed vs what's in the version, bail
        const sameUnlocked = listIdsEqual(nextUnlocked, v.unlockedMedias);
        const sameLocked = listIdsEqual(nextLocked, v.lockedMedias);

        if (sameUnlocked && sameLocked) return;

        // Also guard against re-entrancy by signature
        const unlockedSig = JSON.stringify(nextUnlocked);
        const lockedSig = JSON.stringify(nextLocked);
        const last = lastWriteRef.current;

        if (last && last.tab === tabIndex && last.unlockedSig === unlockedSig && last.lockedSig === lockedSig) {
            return;
        }

        // Perform write and remember it
        handleVersionChange(tabIndex, {
            unlockedMedias: nextUnlocked,
            lockedMedias: nextLocked,
        });

        lastWriteRef.current = { tab: tabIndex, unlockedSig, lockedSig };
        // deps: ONLY the lists and the active tab index
    }, [unlockedItems, lockedItems, tabIndex]);

    // Existing tags across ALL templates (not just the one being edited) offered as
    // suggestions in the tag editor to reduce typo fragmentation of the tag vocabulary
    const tagSuggestions = useMemo(() => distinctInventoryTags(contentInventory ?? []), [contentInventory]);

    // Handler for confirming tag addition
    const handleAddTag = (idx: number, newTag: string) => {
        if (!model) return;

        const value = newTag.trim();

        if (!value) return;

        const v = model.versions[idx];

        // Ignore case-insensitive duplicates already on this version
        if ((v.tags ?? []).some(t => t.tagText.trim().toLowerCase() === value.toLowerCase())) return;

        handleVersionChange(idx, { tags: [...(v.tags ?? []), { tagText: value }] });
    };

    // Handler for removing tag
    const handleRemoveTag = (versionIdx: number, tagIdx: number) => {
        if (!model) return;

        dialog
            .confirm({
                title: 'Delete Tag?',
                message: 'Are you sure you want to delete this tag?',
                ok: { color: 'error', text: 'Delete', variant: 'contained' },
                cancel: { text: 'Cancel', color: 'primary', variant: 'text' },
            })
            .then(() => {
                const v = model.versions[versionIdx];

                handleVersionChange(versionIdx, {
                    tags: (v.tags ?? []).filter((_, i) => i !== tagIdx),
                });
            })
            .catch(() => {
                // Handle deletion error
            });
    };

    const options = useMemo((): AutoCompleteOption[] => {
        const list: AutoCompleteOption[] = [];
        for (let i = 0; i < inventories.length; i += 1) {
            for (let j = 0; j < inventories[i].versions.length; j += 1) {
                const v = inventories[i].versions[j];
                list.push({
                    inventoryName: inventories[i].name,
                    inventoryId: inventories[i].inventoryId,
                    versionId: v.inventorySubId,
                    versionIndex: j,
                    versionName: v.versionName,
                    tags: (v.tags ?? []).map(t => t.tagText).filter(Boolean),
                    text: v.text ? stripHtml(v.text) : '',
                });
            }
        }
        // Sort A-Z by template name, then variation name (case-insensitive); otherwise keep creation order.
        if (sortAlphabetically) {
            list.sort(
                (a, b) =>
                    (a.inventoryName ?? '').localeCompare(b.inventoryName ?? '', undefined, { sensitivity: 'base' }) ||
                    (a.versionName ?? '').localeCompare(b.versionName ?? '', undefined, { sensitivity: 'base' }),
            );
        }
        return list;
    }, [inventories, sortAlphabetically]);

    const toggleSortAlphabetically = () => {
        setSortAlphabetically(prev => {
            const next = !prev;
            writeInventoryTemplateSortAlphabetically(next);
            return next;
        });
    };

    const optionsWithNone = useMemo(
        (): AutoCompleteOption[] => [
            {
                inventoryName: 'None',
                inventoryId: '',
                versionId: -1,
                versionIndex: 0,
                versionName: '',
                tags: [],
                text: '',
            },
            ...options,
        ],
        [options],
    );

    return pageIsLoading || isInventoryLoading ? (
        <Stack alignItems="center" margin={10}>
            <CircularProgress size={64} />
        </Stack>
    ) : (
        <PageContainerComponent subscriber={subscriber} title="Templates">
            {/* Inventory selector + Add */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                            <FormControl
                                fullWidth
                                sx={{
                                    mb: 0,
                                    mt: 0,
                                }}
                            >
                                {/* <InputLabel id="inventory-select-label">Select Template</InputLabel> */}
                                {/* <Select
                                    labelId="inventory-select-label"
                                    label="Select Template"
                                    value={selectedInventoryId}
                                    onChange={handleInventorySelectChange}
                                    // displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {inventories.map(inv => (
                                        <MenuItem key={inv.inventoryId} value={inv.inventoryId}>
                                            {inv.name}
                                        </MenuItem>
                                    ))}
                                </Select> */}
                                <Autocomplete
                                    options={options}
                                    // value: find currently selected by inventoryId
                                    value={options.find(o => o.inventoryId === selectedInventoryId) ?? null}
                                    onChange={(_, newValue) => {
                                        setSelectedOption(newValue);
                                        // if you still want to track selectedInventoryId:
                                        handleInventorySelectChange(Number(newValue?.inventoryId) ?? 0, newValue?.versionIndex ?? 1);
                                    }}
                                    groupBy={option => option.inventoryName}
                                    getOptionLabel={option => `${option.inventoryName} - ${option.versionName}`}
                                    renderOption={(props, option) => (
                                        <li {...props} key={`${option.inventoryId}-${option.versionId}`}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                <Box>
                                                    {option.inventoryName} — {option.versionName}
                                                </Box>
                                                {option.tags.length > 0 && (
                                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                        {option.tags.slice(0, 6).map(tag => (
                                                            <Chip key={tag} size="small" label={tag} variant="outlined" />
                                                        ))}
                                                        {option.tags.length > 6 && (
                                                            <Chip size="small" label={`+${option.tags.length - 6}`} variant="outlined" />
                                                        )}
                                                    </Stack>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                    // search by name, version, tags, message text
                                    filterOptions={(opts, state) => {
                                        const q = state.inputValue.trim().toLowerCase();
                                        if (!q) return opts;

                                        return opts.filter(opt => {
                                            const inName = opt.inventoryName.toLowerCase().includes(q);
                                            const inVersion = opt.versionName.toLowerCase().includes(q);
                                            const inTags = opt.tags?.some(t => t.toLowerCase().includes(q));
                                            const inText = opt.text.toLowerCase().includes(q);
                                            return inName || inVersion || inTags || inText;
                                        });
                                    }}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Select Template"
                                            placeholder="Search by name, version, tag, or text…"
                                            size="small"
                                        />
                                    )}
                                    // Optional: show "None" when cleared
                                    clearOnEscape
                                    isOptionEqualToValue={(opt, val) =>
                                        opt.inventoryId === val.inventoryId && opt.versionId === val.versionId
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs="auto">
                            <Tooltip title="Sort alphabetically or by creation order">
                                <IconButton
                                    size="medium"
                                    onClick={toggleSortAlphabetically}
                                    aria-label="toggle alphabetical sort"
                                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: '38px', width: '38px' }}
                                >
                                    <SortByAlphaIcon fontSize="small" color={sortAlphabetically ? 'primary' : 'secondary'} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs="auto">
                            <Button
                                variant="contained"
                                size="medium"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setOpenAddInventory(true);
                                }}
                                sx={{
                                    height: '38px',
                                }}
                            >
                                Add
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* When no inventory is selected, show nothing else */}
            {selectedInventoryId === '' ? null : (
                <>
                    <Box>
                        <StyledCardTitleBar
                            title="Template Details"
                            isLoading={false}
                            badge={
                                <Tooltip title="Delete This Entire Template Item">
                                    <Button
                                        variant="contained"
                                        color="error"
                                        aria-label="delete template"
                                        onClick={() => {
                                            dialog
                                                .confirm({
                                                    title: 'Delete Template Item?',
                                                    message: 'Are you sure you want to delete this entire Template item?',
                                                    ok: { color: 'error', text: 'Delete', variant: 'contained' },
                                                    cancel: { text: 'Cancel', color: 'primary', variant: 'text' },
                                                })
                                                .then(() => {
                                                    deleteInventoryItem();
                                                })
                                                .catch(() => {
                                                    // Handle deletion error
                                                });
                                        }}
                                        size="small"
                                        startIcon={<DeleteRounded />}
                                    >
                                        Delete
                                    </Button>
                                </Tooltip>
                            }
                        />
                    </Box>

                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Name"
                                        fullWidth
                                        value={model?.name || ''}
                                        onChange={e => handleRootChange('name', e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Versions */}
                    <Box>
                        <StyledCardTitleBar
                            title="Variations"
                            isLoading={false}
                            badge={
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    sx={{ fontWeight: 700 }}
                                    onClick={() => setOpenAddVersion(true)}
                                >
                                    Add
                                </Button>
                            }
                        />
                    </Box>
                    <Card>
                        <CardContent>
                            <Tabs
                                value={tabIndex}
                                onChange={(_, v) => setTabIndex(v)}
                                variant="scrollable"
                                scrollButtons
                                allowScrollButtonsMobile
                                sx={{
                                    mb: 2,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        minWidth: '120px',
                                    },
                                    '& .MuiTabs-indicator': {
                                        height: 4,
                                    },
                                    // background for select tab
                                    '& .Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    },
                                    // hide scroll buttons when not needed
                                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                                        display: 'none',
                                    },
                                }}
                            >
                                {model?.versions.map((v, idx) => (
                                    <Tab
                                        key={v.inventorySubId}
                                        label={
                                            <Grid container spacing={2} alignItems="center" onMouseDown={e => e.stopPropagation()}>
                                                <Grid item xs>
                                                    <Typography
                                                        flexGrow={1}
                                                        variant="body2"
                                                        fontWeight={600}
                                                        onDoubleClick={e => {
                                                            e.stopPropagation();
                                                            setEditingTab(idx);
                                                            setEditingName(v.versionName);
                                                        }}
                                                    >
                                                        {v.versionName}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs="auto">
                                                    <Stack direction="row" spacing={0} alignItems={'center'}>
                                                        <Tooltip title="Rename">
                                                            <EditIcon
                                                                fontSize="small"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    setEditingTab(idx);
                                                                    setEditingName(v.versionName);
                                                                }}
                                                                sx={{
                                                                    p: 0.25,
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </Tooltip>

                                                        {model.versions.length > 1 && (
                                                            <Tooltip title="Delete Variation">
                                                                <CloseIcon
                                                                    fontSize="small"
                                                                    color="error"
                                                                    onClick={e => {
                                                                        e.stopPropagation();

                                                                        dialog
                                                                            .confirm({
                                                                                message: 'Are you sure you want to delete this variation?',
                                                                                title: 'Confirm Delete',
                                                                                ok: {
                                                                                    color: 'error',
                                                                                    text: 'Delete',
                                                                                    variant: 'contained',
                                                                                },
                                                                                cancel: {
                                                                                    text: 'Cancel',
                                                                                    color: 'primary',
                                                                                    variant: 'text',
                                                                                },
                                                                            })
                                                                            .then(() => {
                                                                                e.preventDefault();
                                                                                deleteVersion(idx);
                                                                            })
                                                                            .catch(() => {
                                                                                // Handle error
                                                                            });
                                                                    }}
                                                                    sx={{
                                                                        p: 0.25,
                                                                        cursor: 'pointer',
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </Grid>

                                                {editingTab === idx && (
                                                    <TextInputDialog
                                                        open={true}
                                                        title="Edit Variation Name"
                                                        label="Variation Name"
                                                        contentText={null}
                                                        onClose={() => setEditingTab(null)}
                                                        onSubmit={name => {
                                                            const trimmed = name.trim();
                                                            if (trimmed) handleVersionChange(idx, { versionName: trimmed });
                                                            setEditingTab(null);
                                                        }}
                                                        initialValue={editingName}
                                                        submitButtonText="Save"
                                                    />
                                                )}
                                            </Grid>
                                        }
                                    />
                                ))}
                            </Tabs>

                            {model && selectedVersion && (
                                <Grid container spacing={2} alignItems={'stretch'}>
                                    {/* Labels */}
                                    <Grid item xs={12} md={6}>
                                        <PostLabelMultiSelect
                                            options={onlyFansPostlabels}
                                            loading={labelsIsLoading}
                                            value={onlyFansPostlabels.filter(L =>
                                                (selectedVersion.postLabels ?? []).some(s => s.postLabelId.toString() === L.labelId),
                                            )}
                                            onChange={next => {
                                                const merged = mergePostLabels(selectedVersion.postLabels, next);
                                                handleVersionChange(tabIndex, { postLabels: merged });
                                            }}
                                            title="Post Labels"
                                        />
                                    </Grid>

                                    {/* Tags (inline entry, no window prompt) */}
                                    <Grid item xs={12} md={6}>
                                        <ContentTagsCard
                                            selectedVersion={selectedVersion}
                                            tabIndex={tabIndex}
                                            tagSuggestions={tagSuggestions}
                                            handleAddTag={handleAddTag}
                                            handleRemoveTag={handleRemoveTag}
                                        />
                                    </Grid>

                                    {/* Send/Exclude Lists */}
                                    <Grid item xs={12} md={6}>
                                        <ListMultiSelect
                                            options={lists}
                                            loading={hasMore}
                                            color="primary"
                                            value={lists.filter(L =>
                                                (selectedVersion.sendLists ?? []).some(s => String(s.listId) === String(L.id)),
                                            )}
                                            onChange={next => {
                                                const merged = mergeSendLists(selectedVersion.sendLists, next);
                                                handleVersionChange(tabIndex, { sendLists: merged });
                                            }}
                                            title="Send Lists"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ListMultiSelect
                                            options={lists}
                                            loading={hasMore}
                                            color="error"
                                            value={lists.filter(L =>
                                                (selectedVersion.excludeLists ?? []).some(s => String(s.listId) === String(L.id)),
                                            )}
                                            onChange={next => {
                                                const merged = mergeSendLists(selectedVersion.excludeLists, next);
                                                handleVersionChange(tabIndex, { excludeLists: merged });
                                            }}
                                            title="Exclude Lists"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <ListSingleSelect
                                            key={`autoAdd-${selectedInventoryId}-${selectedVersion.inventorySubId}`}
                                            options={lists.filter(item => item.canAddUsers && item.type === 'custom')}
                                            loading={hasMore}
                                            value={
                                                lists.find(
                                                    s =>
                                                        s.id.toString() === selectedVersion.autoAddList &&
                                                        selectedVersion.autoAddList !== '',
                                                ) ?? null
                                            }
                                            onChange={next => {
                                                handleVersionChange(tabIndex, { autoAddList: next?.id.toString() ?? '' });
                                            }}
                                            title="List To Add Fan Upon Purchases"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <OnlyFansQuillMessageEditor
                                            key={`quill-${selectedInventoryId}-${selectedVersion.inventorySubId}`}
                                            valueOF={selectedVersion.text}
                                            onChangeOF={ofHtml => handleVersionChange(tabIndex, { text: ofHtml })} // store OnlyFans-class HTML
                                            placeholder="Write something…"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <MediaHolder
                                            subscriber={subscriber}
                                            unlockedItems={unlockedItems}
                                            setUnlockedItems={setUnlockedItems}
                                            lockedItems={lockedItems}
                                            setlockedItems={setlockedItems}
                                            isLoading={isInventoryVaultItemsLoading}
                                            taggedCreators={
                                                selectedVersion.creatorTags?.map(item => ({
                                                    id: item.creatorOnlyfansId,
                                                    name: item.creatorName,
                                                })) ?? []
                                            }
                                            onChangeTaggedCreators={next => {
                                                const merged = mergeTaggedCreators(selectedVersion.creatorTags, next);
                                                handleVersionChange(tabIndex, { creatorTags: merged });
                                            }}
                                            taggedReleaseForms={
                                                selectedVersion.additionalSettings?.consentForms?.map(item => ({
                                                    id: item.id,
                                                    name: item.name,
                                                })) ?? []
                                            }
                                            setTaggedReleaseForms={update => {
                                                const prevForms =
                                                    selectedVersion.additionalSettings?.consentForms?.map(item => ({
                                                        id: item.id,
                                                        name: item.name,
                                                    })) ?? [];
                                                const next = typeof update === 'function' ? update(prevForms) : update;
                                                const mergedConsentForms = mergeConsentForms(
                                                    selectedVersion.additionalSettings?.consentForms,
                                                    next,
                                                );
                                                handleVersionChange(tabIndex, {
                                                    additionalSettings: {
                                                        ...selectedVersion.additionalSettings,
                                                        consentForms: mergedConsentForms,
                                                    },
                                                });
                                            }}
                                            lockedPrice={selectedVersion.price.toString()}
                                            onPriceChanged={newPrice => handleVersionChange(tabIndex, { price: newPrice })}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Accordion disableGutters variant="outlined">
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Additional Settings
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <TextField
                                                    label="Unsend After"
                                                    type="number"
                                                    size="small"
                                                    value={selectedVersion.additionalSettings?.autoUnsendHours ?? ''}
                                                    onChange={e => {
                                                        const raw = e.target.value;
                                                        const num = Number(raw);
                                                        const autoUnsendHours =
                                                            raw.trim() === '' || Number.isNaN(num) ? null : Math.max(0, num);
                                                        handleVersionChange(tabIndex, {
                                                            additionalSettings: {
                                                                ...selectedVersion.additionalSettings,
                                                                autoUnsendHours,
                                                            },
                                                        });
                                                    }}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                                                        inputProps: { min: 0 },
                                                    }}
                                                />
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Footer actions */}
                            <Divider sx={{ my: 2 }} />

                            <Stack spacing={1}>
                                <Tooltip title={getPostToOnlyFansDisabledReason() || 'Post this variation to OnlyFans'}>
                                    <span>
                                        <LoadingButton
                                            variant="outlined"
                                            size="medium"
                                            fullWidth
                                            startIcon={<PostAddIcon />}
                                            onClick={handlePostToOnlyFans}
                                            disabled={!!getPostToOnlyFansDisabledReason()}
                                            loading={isPosting}
                                        >
                                            <span>Post to OnlyFans</span>
                                        </LoadingButton>
                                    </span>
                                </Tooltip>

                                <LoadingButton
                                    variant="contained"
                                    size="medium"
                                    fullWidth
                                    startIcon={<SaveIcon />}
                                    onClick={save}
                                    disabled={!isDirty}
                                    loading={isSaving}
                                >
                                    <span>Save</span>
                                </LoadingButton>
                            </Stack>
                        </CardContent>
                    </Card>

                    <InventoryPostSuccessDialog open={postSuccessOpen} postUrl={postedUrl} onClose={() => setPostSuccessOpen(false)} />

                    {/* Add Version dialog */}
                    <TextInputDialog
                        submitButtonText="Add"
                        cancelButtonText="Cancel"
                        open={openAddVersion}
                        title="Add New Variation"
                        label="Variation name"
                        contentText={null}
                        onClose={() => {
                            setOpenAddVersion(false);
                            setNewVersionSourceSubId('');
                        }}
                        onSubmit={name => {
                            addVersion(name, newVersionSourceSubId !== '' ? newVersionSourceSubId : undefined);
                        }}
                        initialValue=""
                    >
                        <Divider sx={{ mt: 2, mb: 1.5 }} />
                        <FormControl fullWidth size="small">
                            <InputLabel id="duplicate-version-label">Duplicate settings from variation</InputLabel>
                            <Select
                                labelId="duplicate-version-label"
                                value={newVersionSourceSubId}
                                label="Duplicate settings from variation"
                                onChange={e => setNewVersionSourceSubId(e.target.value as number | '')}
                            >
                                <MenuItem value="">None (start blank)</MenuItem>
                                {model?.versions.map(v => (
                                    <MenuItem key={v.inventorySubId} value={v.inventorySubId}>
                                        {v.versionName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </TextInputDialog>
                    {/* <SimpleTextDialog
                        open={openAddVersion}
                        title="Add Variation"
                        label="Variation name"
                        onClose={() => setOpenAddVersion(false)}
                        onSave={name => addVersion(name)}
                    /> */}
                </>
            )}
            {/* Add Inventory dialog */}
            <TextInputDialog
                submitButtonText="Create Blank Template"
                cancelButtonText="Cancel"
                open={openAddInventory}
                title="Create New Template"
                label="Template name"
                contentText={null}
                onClose={() => {
                    setOpenAddInventory(false);
                    setNewTemplateName('');
                    setShowMassDmList(false);
                }}
                onValueChange={v => setNewTemplateName(v)}
                onSubmit={name => {
                    addInventoryItem(name);
                    setNewTemplateName('');
                }}
                initialValue=""
                maxWidth="md"
                showErrorOnEmpty
            >
                <Divider sx={{ mt: 2, mb: 1.5 }} />
                <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={() => setShowMassDmList(v => !v)}
                    sx={{ mb: showMassDmList ? 1.5 : 0 }}
                >
                    {showMassDmList ? 'Hide Previously Sent Mass DMs' : 'Use Previously Sent Mass DM to Create Template'}
                </Button>
                {showMassDmList && (
                    <>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search mass DMs…"
                            value={massDmSearchInput}
                            onChange={e => {
                                setMassDmSearchInput(e.target.value);
                                debouncedSetQuery(e.target.value);
                            }}
                            sx={{ mb: 1.5 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: massDmSearchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setMassDmSearchInput('');
                                                setOffset(0);
                                                setAccumulatedMassDms([]);
                                                setQuery('');
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                        {isMassDmsLoading && offset === 0 && (
                            <Stack spacing={1}>
                                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                            </Stack>
                        )}
                        {!isMassDmsLoading && massDms && massDms.list.length === 0 && accumulatedMassDms.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No sent Mass DMs found
                            </Typography>
                        )}
                        {(accumulatedMassDms.length > 0 || isMassDmsLoading) && (
                            <Box
                                ref={massDmScrollRef}
                                onScroll={handleMassDmScroll}
                                sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}
                            >
                                {accumulatedMassDms.map(massDm => (
                                    <Box
                                        key={massDm._id || massDm.massDmId}
                                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                                    >
                                        <Stack direction="column" spacing={0.75}>
                                            {/* Text — full width on all screen sizes, priority content */}
                                            <Typography
                                                variant="body2"
                                                onClick={() => handleOpenDmPreview(massDm)}
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: { xs: 3, sm: 2 },
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    cursor: massDm.messageDetails?.rawText ? 'pointer' : 'default',
                                                    '&:hover': massDm.messageDetails?.rawText
                                                        ? { textDecoration: 'underline', textDecorationStyle: 'dotted' }
                                                        : {},
                                                }}
                                            >
                                                {massDm.messageDetails?.rawText || <em>No text</em>}
                                            </Typography>

                                            {/* Stats row + button */}
                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={{ xs: 0.5, sm: 1 }}
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                                justifyContent="space-between"
                                            >
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                    {massDm.messageDetails?.mediaCount !== undefined &&
                                                        massDm.messageDetails.mediaCount > 0 && (
                                                            <Chip
                                                                size="small"
                                                                label={`Media: ${massDm.messageDetails.mediaCount}`}
                                                                onClick={() => handleOpenDmPreview(massDm)}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {moment(massDm.date).format('L LT')}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        Sent: {massDm.sentCount?.toLocaleString() ?? '—'}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        Viewed: {massDm.viewedCount?.toLocaleString() ?? '—'}
                                                    </Typography>
                                                    {!massDm.isFree && (
                                                        <Typography variant="caption" color="error">
                                                            Price: {massDm.messageDetails?.price?.toLocaleString() ?? '—'}
                                                        </Typography>
                                                    )}
                                                    {!massDm.isFree && (
                                                        <Typography variant="caption">
                                                            Purchased: {massDm.purchasedCount?.toLocaleString() ?? '—'}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    fullWidth={false}
                                                    disabled={newTemplateName.trim() === ''}
                                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
                                                    onClick={() => {
                                                        addInventoryItem(newTemplateName, massDm.messageDetails?.id);
                                                    }}
                                                >
                                                    Create using this PPV
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                ))}
                                {isMassDmsLoading && (
                                    <Stack alignItems="center" py={1}>
                                        <CircularProgress size={20} />
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </TextInputDialog>
            {/* Combined text + media + price preview dialog */}
            <Dialog
                open={massDmExpandedDm !== null}
                onClose={() => {
                    setMassDmExpandedDm(null);
                    setMassDmMediaPreviewItems(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <StyledCardTitleBar title="Message Details" />
                <DialogContent>
                    {/* Price */}
                    {!massDmExpandedDm?.isFree && massDmExpandedDm?.messageDetails?.price != null && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Price:
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="error">
                                $
                                {massDmExpandedDm.messageDetails.price.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </Typography>
                        </Stack>
                    )}
                    {/* Text */}
                    {massDmExpandedDm?.messageDetails?.rawText ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {massDmExpandedDm.messageDetails.rawText}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            <em>No text</em>
                        </Typography>
                    )}
                    {/* Media */}
                    {(massDmExpandedDm?.messageDetails?.mediaCount ?? 0) > 0 && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            {isMassDmMediaLoading ? (
                                <Stack direction="row" spacing={1}>
                                    {[0, 1, 2].map(i => (
                                        <Skeleton
                                            key={i}
                                            variant="rectangular"
                                            width={150}
                                            height={150}
                                            sx={{ borderRadius: 1, flexShrink: 0 }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                (() => {
                                    const allItems = massDmMediaPreviewItems ?? [];
                                    const isAllFree = massDmExpandedDm?.messageDetails?.price === 0;
                                    const freeItems = isAllFree ? [] : allItems.filter(item => massDmMediaPreviews.has(item.id));
                                    const lockedItems = isAllFree ? [] : allItems.filter(item => !massDmMediaPreviews.has(item.id));
                                    return (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flexWrap: 'nowrap',
                                                gap: 1.5,
                                                overflowX: 'auto',
                                                pb: 1,
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            {isAllFree && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        flexWrap: 'nowrap',
                                                        gap: 1,
                                                        overflowX: 'auto',
                                                        overflowY: 'hidden',
                                                        WebkitOverflowScrolling: 'touch',
                                                        height: 150,
                                                    }}
                                                >
                                                    {allItems.map(item => (
                                                        <Box
                                                            key={item.id}
                                                            sx={{ flexShrink: 0, width: 150, height: 150, overflow: 'hidden' }}
                                                        >
                                                            <VaultTile
                                                                subscriber={subscriber}
                                                                item={item}
                                                                isSelected={false}
                                                                handleMediaClicked={() => {}}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                            {!isAllFree && freeItems.length > 0 && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                                                    {/* Tab label — in normal flow so mb: '-1px' actually works */}
                                                    <Box
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            px: 1.5,
                                                            py: 0.25,
                                                            borderTopLeftRadius: '8px',
                                                            borderTopRightRadius: '8px',
                                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="success.main" fontWeight={700}>
                                                            Free
                                                        </Typography>
                                                    </Box>
                                                    {/* Content box — no top-left radius so it merges with tab */}
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: '0 8px 8px 8px',
                                                            bgcolor: alpha(theme.palette.success.main, 0.1),
                                                            height: 168,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                flexWrap: 'nowrap',
                                                                gap: 1,
                                                                overflowX: 'auto',
                                                                overflowY: 'hidden',
                                                                WebkitOverflowScrolling: 'touch',
                                                                height: 150,
                                                            }}
                                                        >
                                                            {freeItems.map(item => (
                                                                <Box
                                                                    key={item.id}
                                                                    sx={{ flexShrink: 0, width: 150, height: 150, overflow: 'hidden' }}
                                                                >
                                                                    <VaultTile
                                                                        subscriber={subscriber}
                                                                        item={item}
                                                                        isSelected={false}
                                                                        handleMediaClicked={() => {}}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                            {!isAllFree && freeItems.length > 0 && lockedItems.length > 0 && (
                                                <Divider orientation="vertical" flexItem />
                                            )}
                                            {!isAllFree && lockedItems.length > 0 && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                                                    {/* Tab label — in normal flow so mb: '-1px' actually works */}
                                                    <Box
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            px: 1.5,
                                                            py: 0.25,
                                                            borderTopLeftRadius: '8px',
                                                            borderTopRightRadius: '8px',
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            zIndex: 1,
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="primary.main" fontWeight={700}>
                                                            Locked
                                                        </Typography>
                                                    </Box>
                                                    {/* Content box — no top-left radius so it merges with tab */}
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: '0 8px 8px 8px',
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            height: 168,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                flexWrap: 'nowrap',
                                                                gap: 1,
                                                                overflowX: 'auto',
                                                                overflowY: 'hidden',
                                                                WebkitOverflowScrolling: 'touch',
                                                                height: 150,
                                                            }}
                                                        >
                                                            {lockedItems.map(item => (
                                                                <Box
                                                                    key={item.id}
                                                                    sx={{ flexShrink: 0, width: 150, height: 150, overflow: 'hidden' }}
                                                                >
                                                                    <VaultTile
                                                                        subscriber={subscriber}
                                                                        item={item}
                                                                        isSelected={false}
                                                                        handleMediaClicked={() => {}}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })()
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setMassDmExpandedDm(null);
                            setMassDmMediaPreviewItems(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Discard unsaved changes?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">You have unsaved changes. Switching templates will lose them.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            setConfirmOpen(false);
                            applyInventorySelection(pendingInventoryId);
                        }}
                    >
                        Discard changes
                    </Button>
                </DialogActions>
            </Dialog>
            <NavigationGuard when={isDirty} />
        </PageContainerComponent>
    );
};

export default ContentInventoryPage;
